/**
 * Data Recovery Service
 *
 * Detects and recovers from localStorage data loss using Supabase as backup.
 * Called on app init for Profile users after ensureProfileSynced().
 *
 * Two modes:
 * - Total loss: profile_user_id exists but profile_user_data missing → full restore
 * - Partial loss: server rooms missing from this phone → fill gaps (id set diff),
 *   plus rooms both tapped into that this phone never recorded (formedRecovery)
 */

import { supabase } from '../supabase';
import { getProfileUserId } from '../../utils/identityUtils';
import { CONNECTION_METADATA_PREFIX, MANUAL_CONTACT_DATA_PREFIX } from './userDataStorage';
import {
  logRecoveryTriggered,
  logRecoveryCompleted,
  logRecoveryError,
} from '../analytics/eventLogger';
import { recoverFormedRooms } from '../exchange/formedRecovery';

/**
 * Check for data loss and recover from Supabase if needed
 * @returns {Promise<boolean>} true if recovery ran, false if no recovery needed
 */
export const checkAndRecoverData = async () => {
  const profileUserId = getProfileUserId();
  if (!profileUserId) return false;

  try {
    const hasProfileData = !!localStorage.getItem('profile_user_data');

    if (!hasProfileData) {
      // Total loss — profile_user_id exists but profile_user_data is gone
      console.log('[RECOVERY] Total loss detected — starting full recovery');
      return await recoverFullData(profileUserId);
    }

    // Partial loss check (background, non-blocking)
    topUpMissingConnections(profileUserId);
    return false;
  } catch (error) {
    console.error('[RECOVERY] checkAndRecoverData failed:', error);
    logRecoveryError('check', error.message);
    return false;
  }
};

/**
 * Full recovery — restore all data from Supabase
 */
const recoverFullData = async (profileUserId) => {
  try {
    logRecoveryTriggered('total', 0);

    // 1. Restore profile data
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('profile_data, user_preferences')
      .eq('profile_user_id', profileUserId)
      .maybeSingle();

    if (profileError) {
      logRecoveryError('fetch_profile', profileError.message);
      return false;
    }

    if (profile?.profile_data) {
      // Hydrate profile_user_data from the jsonb blob
      localStorage.setItem('profile_user_data', JSON.stringify(profile.profile_data));
      console.log('[RECOVERY] Restored profile_user_data');
    } else {
      // No profile_data yet (e.g., pending conversion — profile created by RPC,
      // user hasn't filled out onboarding). Skip profile restore but continue
      // to connection data recovery below.
      console.log('[RECOVERY] No profile_data in Supabase — skipping profile restore');
    }

    // Hydrate user preferences
    if (profile?.user_preferences) {
      const prefs = profile.user_preferences;
      if (prefs.starred_connections) {
        localStorage.setItem(
          'profile_starred_connections',
          JSON.stringify(prefs.starred_connections)
        );
      }
      if (prefs.connections_sort_mode) {
        localStorage.setItem('connections_sort_mode', prefs.connections_sort_mode);
      }
      if (prefs.profile_name_sharing) {
        localStorage.setItem('profile_name_sharing', prefs.profile_name_sharing);
      }
      console.log('[RECOVERY] Restored user preferences');
    }

    // 2. Restore all connection data
    const connectionsRestored = await restoreConnectionData(profileUserId);

    // 3. Re-enable demo mode (required for ProfileConnectionsList)
    localStorage.setItem('demo_mode', 'true');

    logRecoveryCompleted(connectionsRestored, 0);
    console.log('[RECOVERY] Full recovery complete:', connectionsRestored, 'connections restored');

    // Only trigger App.js reload if profile_data was restored.
    // For pending conversions (no profile_data), connections are restored
    // but hydrateFromAuth() handles the reload — returning true here would loop.
    return !!profile?.profile_data;
  } catch (error) {
    console.error('[RECOVERY] Full recovery failed:', error);
    logRecoveryError('full_recovery', error.message);
    return false;
  }
};

/**
 * Restore connection data rows from Supabase to localStorage
 * @returns {number} Number of connections restored
 */
const restoreConnectionData = async (profileUserId) => {
  const { data: rows, error } = await supabase
    .from('connection_data')
    .select('*')
    .eq('profile_user_id', profileUserId);

  if (error) {
    logRecoveryError('fetch_connections', error.message);
    return 0;
  }

  if (!rows || rows.length === 0) return 0;

  let restored = 0;
  for (const row of rows) {
    hydrateConnectionRow(row);
    restored++;
  }

  return restored;
};

/**
 * Hydrate localStorage keys from a single connection_data row
 */
const hydrateConnectionRow = (row) => {
  const id = row.chat_room_id;

  // Discovery keys (must be first — connectionStorage scans for these)
  if (row.is_manual && row.manual_contact) {
    localStorage.setItem(`manual_contact_data_${id}`, JSON.stringify(row.manual_contact));
  }
  if (row.metadata) {
    localStorage.setItem(`connection_metadata_${id}`, JSON.stringify(row.metadata));
  }

  // Contact data
  if (row.received_contact && !row.is_manual) {
    localStorage.setItem(`received_anon_data_${id}`, JSON.stringify(row.received_contact));
  }

  // Per-connection fields
  if (row.notes) {
    localStorage.setItem(`profile_notes_${id}`, JSON.stringify(row.notes));
  }
  if (row.sent_shared_fields) {
    localStorage.setItem(`sent_shared_fields_${id}`, JSON.stringify(row.sent_shared_fields));
  }
  if (row.name_sharing) {
    localStorage.setItem(`name_sharing_${id}`, row.name_sharing);
  }
  if (row.contact_shared) {
    localStorage.setItem(`contact_shared_${id}`, 'true');
  }
  if (row.contact_added) {
    localStorage.setItem(`contact_added_${id}`, 'true');
  }
  if (row.contact_card_sent) {
    localStorage.setItem(`contact_card_sent_${id}`, 'true');
  }

  // Reset last_read to now (marks all messages as read on recovery)
  localStorage.setItem(`last_read_${id}`, new Date().toISOString());
};

/** Room ids this phone already holds (exchange + hand-typed records). */
const localRoomIds = () => {
  const ids = new Set();
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith(CONNECTION_METADATA_PREFIX))
      ids.add(key.slice(CONNECTION_METADATA_PREFIX.length));
    else if (key.startsWith(MANUAL_CONTACT_DATA_PREFIX))
      ids.add(key.slice(MANUAL_CONTACT_DATA_PREFIX.length));
  }
  return ids;
};

/**
 * Hydrate every connection the server holds for this profile that the phone
 * doesn't have yet — a set diff on room ids (one id-only read), then full rows
 * for just the missing ones. Replaces the old count comparison, which stale
 * local entries could mask (f/simple-merge: merged rooms must land in People).
 * @returns {Promise<number>} how many connections were restored
 */
const topUpsInFlight = new Map();
export const topUpMissingConnections = (profileUserId) => {
  // Login starts a top-up in the background and then awaits one: share it
  if (!topUpsInFlight.has(profileUserId)) {
    topUpsInFlight.set(
      profileUserId,
      runTopUp(profileUserId).finally(() => topUpsInFlight.delete(profileUserId))
    );
  }
  return topUpsInFlight.get(profileUserId);
};

const runTopUp = async (profileUserId) => {
  try {
    const { data: ids, error } = await supabase
      .from('connection_data')
      .select('chat_room_id')
      .eq('profile_user_id', profileUserId);
    if (error || !ids) {
      if (error) console.warn('[RECOVERY] Top-up id read failed:', error.message);
      return 0;
    }

    const local = localRoomIds();
    const missing = ids.map((r) => r.chat_room_id).filter((id) => !local.has(id));
    // A room both tapped into but this phone never recorded is in neither set
    const known = new Set([...local, ...ids.map((r) => r.chat_room_id)]);
    const formed = await recoverFormedRooms(profileUserId, known);
    if (missing.length === 0) return formed;
    logRecoveryTriggered('partial', missing.length);

    const { data: rows, error: rowError } = await supabase
      .from('connection_data')
      .select('*')
      .eq('profile_user_id', profileUserId)
      .in('chat_room_id', missing);
    if (rowError || !rows) {
      if (rowError) console.warn('[RECOVERY] Top-up fetch failed:', rowError.message);
      return 0;
    }

    rows.forEach(hydrateConnectionRow);
    logRecoveryCompleted(rows.length, 0);
    console.log('[RECOVERY] Top-up: restored', rows.length, 'missing connections');
    return rows.length + formed;
  } catch (error) {
    console.warn('[RECOVERY] Top-up error:', error);
    return 0;
  }
};
